import React, { useMemo } from 'react';
import { Badge } from '@mui/material';
import {
  Book,
  Campaign,
  Close,
  History,
  Inventory,
  LocalOffer,
  Person,
  PersonAdd,
  ShoppingBag,
} from '@mui/icons-material';

export default function useDashboardActionButtons({
  customerRequired,
  selectedCustomer,
  setIsCustomerModalOpen,
  handleHoldBill,
  disableActionShortcuts,
  inExchangeWorkflow,
  carryBags,
  setIsCarryBagModalOpen,
  bill_discount_id,
  orderCartItems,
  currentOrderId,
  cart,
  setIsDiscountDrawerOpen,
  setIsHoldDrawerOpen,
  isCatalogView,
  setIsCatalogView,
  setCatalogFocusIndex,
  setCatalogFocusZone,
  salespersonRequired,
  selectedSalesperson,
  setIsSalespersonDrawerOpen,
  applied_promotions,
  setIsPromotionsModalOpen,
  inReturnMode,
}) {
  return useMemo(() => [
    {
      label: 'Customer',
      shortcut: 'F2',
      icon: <PersonAdd fontSize="small" />,
      color: customerRequired && !selectedCustomer ? '#EF4444' : '#6366F1',
      onClick: () => setIsCustomerModalOpen(true),
      required: customerRequired && !selectedCustomer,
      disabled: disableActionShortcuts || inExchangeWorkflow || inReturnMode,
    },
    {
      label: 'Hold Bill',
      shortcut: 'F4',
      icon: <History fontSize="small" />,
      color: '#64748B',
      onClick: handleHoldBill,
      disabled: disableActionShortcuts || inExchangeWorkflow || inReturnMode,
    },
    {
      label: 'Carry Bag',
      shortcut: 'F8',
      icon: <ShoppingBag fontSize="small" />,
      color: carryBags.length > 0 ? '#F59E0B' : '#CBD5E1',
      onClick: carryBags.length > 0 ? () => setIsCarryBagModalOpen(true) : undefined,
      disabled: disableActionShortcuts || inExchangeWorkflow || inReturnMode || carryBags.length === 0,
    },
    {
      label: 'Discount',
      shortcut: null,
      icon: <LocalOffer fontSize="small" />,
      color: (bill_discount_id || orderCartItems.some((item) => item.discount_id))
        ? '#16A34A'
        : (currentOrderId && cart.length > 0 ? '#4F46E5' : '#CBD5E1'),
      onClick: currentOrderId && cart.length > 0 ? () => setIsDiscountDrawerOpen(true) : undefined,
      disabled: disableActionShortcuts || !currentOrderId || cart.length === 0 || inExchangeWorkflow || inReturnMode,
    },
    {
      label: 'Held',
      shortcut: 'F5',
      icon: <Inventory fontSize="small" />,
      color: '#10B981',
      onClick: () => setIsHoldDrawerOpen(true),
      disabled: disableActionShortcuts || inExchangeWorkflow || inReturnMode,
    },
    {
      label: isCatalogView ? 'Close' : 'Catalog',
      shortcut: 'F3',
      icon: isCatalogView ? <Close fontSize="small" /> : <Book fontSize="small" />,
      color: isCatalogView ? '#EF4444' : '#F59E0B',
      onClick: () => {
        setIsCatalogView((prev) => !prev);
        setCatalogFocusIndex(-1);
        setCatalogFocusZone('tabs');
      },
      disabled: disableActionShortcuts || inReturnMode,
    },
    {
      label: 'Sales Person',
      shortcut: 'F6',
      icon: <Person fontSize="small" />,
      color: selectedSalesperson ? '#10B981' : (salespersonRequired ? '#EF4444' : '#64748B'),
      onClick: () => setIsSalespersonDrawerOpen(true),
      required: salespersonRequired && !selectedSalesperson,
      // Enabled on exchanges too — Dashboard routes the pick to the exchange's own salesperson.
      disabled: disableActionShortcuts || inReturnMode,
    },
    {
      label: 'Promotions',
      shortcut: 'F9',
      icon: (
        <Badge
          badgeContent={applied_promotions.length}
          color="success"
          invisible={applied_promotions.length === 0}
          sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', minWidth: 14, height: 14, p: 0 } }}
        >
          <Campaign fontSize="small" />
        </Badge>
      ),
      color: applied_promotions.length > 0 ? '#16A34A' : '#6366F1',
      onClick: () => setIsPromotionsModalOpen(true),
      disabled: disableActionShortcuts || inExchangeWorkflow || inReturnMode,
    },
  ], [
    applied_promotions,
    bill_discount_id,
    cart.length,
    carryBags.length,
    currentOrderId,
    customerRequired,
    disableActionShortcuts,
    handleHoldBill,
    inExchangeWorkflow,
    inReturnMode,
    isCatalogView,
    orderCartItems,
    salespersonRequired,
    selectedCustomer,
    selectedSalesperson,
    setIsCarryBagModalOpen,
    setIsCatalogView,
    setIsCustomerModalOpen,
    setIsDiscountDrawerOpen,
    setIsHoldDrawerOpen,
    setIsPromotionsModalOpen,
    setIsSalespersonDrawerOpen,
    setCatalogFocusIndex,
    setCatalogFocusZone,
  ]);
}
